import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const fmtCurrency = (val) => {
  if (val === null || val === undefined || Number.isNaN(val)) return '—';
  return val.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

/**
 * Sezione Redditività riusabile: Store / Area / Panoramica.
 *
 * `profitability` shape:
 *   { revenue, total_costs, net_result, margin_pct,
 *     cost_breakdown: { categoria: importo },
 *     cost_category_types: { categoria: 'recurring' | 'one_off' | 'mixed' } }
 *
 * Note: `revenue` è IVA inclusa. La sezione applica IVA 22% e mostra il risultato
 * sui ricavi netti per coerenza con la pagina Negozio.
 */
export default function ProfitabilitySection({ profitability, periodLabel, title = 'Redditività', testId = 'profitability-section' }) {
  if (!profitability) return null;
  const revenue = profitability.revenue || 0;
  const vat = revenue * 0.22;
  const netRevenue = revenue * 0.78;
  const totalCosts = profitability.total_costs || 0;
  const netResult = netRevenue - totalCosts;
  const marginPct = netRevenue > 0 ? (netResult / netRevenue * 100).toFixed(1) : '0.0';
  const breakdown = profitability.cost_breakdown || {};
  const types = profitability.cost_category_types || {};
  const orderedEntries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);

  return (
    <TooltipProvider delayDuration={150}>
    <div className="bg-white border border-slate-200 p-5" data-testid={testId}>
      <h3 className="font-heading text-base font-semibold mb-4">
        {title}{periodLabel ? <span className="text-slate-400 font-normal"> — {periodLabel}</span> : null}
      </h3>
      <div className="space-y-3">
        <ProfitRow
          label="Ricavi operativi (IVA inclusa)"
          info="Somma di tutti i ricavi del periodo, IVA inclusa, da Fresha."
          value={revenue}
        />
        <ProfitRow
          label="IVA 22%"
          info="Ricavi operativi × 22% — sottratta per ottenere i ricavi netti."
          value={-vat}
        />
        <ProfitRow
          label="Ricavi netti"
          info="Ricavi operativi − IVA (equivalente a Ricavi × 0.78)."
          value={netRevenue}
          highlight
        />
        {orderedEntries.map(([cat, val]) => {
          const ct = types[cat];
          const badges = [];
          if (ct === 'one_off') badges.push({ text: 'Una tantum', color: 'amber' });
          else if (ct === 'mixed') badges.push({ text: 'Misto', color: 'slate' });
          // Soglia incidenza: una singola voce di costo > 50% dei ricavi netti
          // è considerata "alta incidenza" e segnalata al management.
          const incidence = netRevenue > 0 ? (val / netRevenue) * 100 : null;
          if (incidence != null && incidence > 50) {
            badges.push({ text: 'Alta incidenza', color: 'red' });
          }
          return (
            <ProfitRow
              key={cat}
              label={cat}
              info={`Costi della categoria "${cat}" registrati nel periodo (importazione costi).`}
              value={-val}
              badges={badges}
              subLabel={incidence != null ? `${incidence.toFixed(1)}% sui ricavi netti` : null}
              subLabelColor="text-red-700"
              subLabelInfo={incidence != null ? `${cat} ÷ Ricavi netti × 100` : null}
            />
          );
        })}
        {orderedEntries.length === 0 && (
          <p className="text-xs text-slate-400 italic">Nessun costo registrato nel periodo.</p>
        )}
        <div className="border-t border-slate-200 pt-3">
          <ProfitRow
            label="Risultato economico"
            info="Ricavi netti − Totale costi del periodo."
            value={netResult}
            highlight
          />
        </div>
        {revenue > 0 && (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              Margine (su ricavi netti)
              <FormulaIcon label="Margine" formula="Risultato economico ÷ Ricavi netti × 100" />
            </span>
            <span className={`font-medium ${parseFloat(marginPct) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`${testId}-margin`}>
              {parseFloat(marginPct) > 0 ? '+' : ''}{marginPct}%
            </span>
          </div>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}

function FormulaIcon({ label, formula, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex align-middle opacity-40 hover:opacity-100 focus:opacity-100 focus:outline-none"
          aria-label={`Formula ${label}`}
          data-testid={testId}
        >
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <span className="font-medium">{label}</span>: {formula}
      </TooltipContent>
    </Tooltip>
  );
}

function ProfitRow({ label, info, value, highlight, badges, subLabel, subLabelColor, subLabelInfo }) {
  const color = value > 0 ? 'text-emerald-700' : value < 0 ? 'text-red-700' : 'text-slate-500';
  const cls = highlight
    ? 'flex items-start justify-between text-sm font-semibold py-1.5'
    : 'flex items-start justify-between text-sm py-0.5';
  const badgeClassFor = (color) => {
    if (color === 'amber') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (color === 'red') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-50 text-slate-600 border-slate-200';
  };
  return (
    <div className={cls}>
      <span className="text-slate-700 flex items-center gap-2 flex-wrap min-w-0">
        <span className="flex items-center gap-1.5">
          {label}
          {info && <FormulaIcon label={label} formula={info} />}
        </span>
        {(badges || []).map((b) => (
          <span key={b.text} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${badgeClassFor(b.color)}`}>
            {b.text}
          </span>
        ))}
      </span>
      <span className="flex flex-col items-end whitespace-nowrap">
        <span className={`font-mono ${color}`}>
          {fmtCurrency(Math.abs(value || 0))}{value < 0 ? ' -' : ''}
        </span>
        {subLabel && (
          <span className={`text-[10px] font-medium mt-0.5 flex items-center gap-1 ${subLabelColor || 'text-slate-400'}`}>
            {subLabel}
            {subLabelInfo && <FormulaIcon label={subLabel} formula={subLabelInfo} />}
          </span>
        )}
      </span>
    </div>
  );
}
